import { useState } from "react";
import { Alert, Button, Form, Input } from "antd";
import { LockOutlined, UserOutlined } from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

interface LoginForm {
  username: string;
  password: string;
}

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const from = (loc.state as { from?: string } | null)?.from ?? "/";

  async function onFinish(values: LoginForm) {
    setErr(null);
    setBusy(true);
    try {
      await login(values.username, values.password);
      nav(from, { replace: true });
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background:
          "radial-gradient(900px 600px at 85% -10%, rgba(54,197,240,.10), transparent 60%), " +
          "radial-gradient(1000px 700px at -10% 30%, rgba(0,212,163,.07), transparent 60%), " +
          "#070a14",
        padding: 16,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Cyan glow top-right */}
      <div
        style={{
          position: "absolute",
          top: -160,
          right: -160,
          width: 480,
          height: 480,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(54,197,240,0.14) 0%, rgba(54,197,240,0) 70%)",
          pointerEvents: "none",
        }}
      />
      {/* Green glow bottom-left */}
      <div
        style={{
          position: "absolute",
          bottom: -160,
          left: -160,
          width: 480,
          height: 480,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(0,212,163,0.10) 0%, rgba(0,212,163,0) 70%)",
          pointerEvents: "none",
        }}
      />

      {/* Subtle grid */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          pointerEvents: "none",
          backgroundImage:
            "linear-gradient(rgba(54,197,240,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(54,197,240,0.03) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
        }}
      />

      {/* Card */}
      <div
        style={{
          position: "relative",
          width: "100%",
          maxWidth: 400,
          background: "rgba(11,16,32,0.82)",
          backdropFilter: "blur(22px)",
          WebkitBackdropFilter: "blur(22px)",
          border: "1px solid rgba(54,197,240,0.18)",
          borderRadius: 18,
          padding: "40px 34px 32px",
          boxShadow:
            "0 28px 90px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.03) inset, 0 0 60px rgba(54,197,240,0.07)",
        }}
      >
        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div
            style={{
              display: "inline-flex",
              width: 64,
              height: 64,
              background: "linear-gradient(135deg, #36c5f0 0%, #00d4a3 100%)",
              borderRadius: 18,
              alignItems: "center",
              justifyContent: "center",
              marginBottom: 18,
              boxShadow:
                "0 12px 36px rgba(54,197,240,0.38), 0 0 0 1px rgba(255,255,255,0.08) inset",
            }}
          >
            <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="#001018" strokeWidth="2.2" strokeLinecap="round">
              <path d="M2 12h4l3-7 4 14 3-7h6"/>
            </svg>
          </div>
          <div
            style={{
              fontSize: 22,
              fontWeight: 700,
              color: "#ffffff",
              letterSpacing: 0.4,
              marginBottom: 4,
              fontFamily: 'Inter, -apple-system, sans-serif',
            }}
          >
            NetScope Console
          </div>
          <div style={{ fontSize: 12.5, color: "#8592b8", letterSpacing: 0.3 }}>
            Network Flow Analytics · Sign in
          </div>
        </div>

        {/* Error */}
        {err && (
          <Alert
            type="error"
            message={err}
            showIcon
            closable
            style={{
              marginBottom: 16,
              background: "rgba(255,93,108,0.1)",
              border: "1px solid rgba(255,93,108,0.28)",
              borderRadius: 8,
              color: "#fca5a5",
            }}
            onClose={() => setErr(null)}
          />
        )}

        {/* Form */}
        <Form<LoginForm> layout="vertical" onFinish={onFinish} autoComplete="off">
          <Form.Item
            name="username"
            rules={[{ required: true, message: "Username required" }]}
            style={{ marginBottom: 12 }}
          >
            <Input
              prefix={<UserOutlined style={{ color: "#8592b8" }} />}
              placeholder="Username"
              size="large"
              style={{
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(54,197,240,0.15)",
                borderRadius: 10,
                color: "#dde4f5",
                fontSize: 13,
              }}
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[{ required: true, message: "Password required" }]}
            style={{ marginBottom: 22 }}
          >
            <Input.Password
              prefix={<LockOutlined style={{ color: "#8592b8" }} />}
              placeholder="Password"
              size="large"
              style={{
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(54,197,240,0.15)",
                borderRadius: 10,
                color: "#dde4f5",
                fontSize: 13,
              }}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              type="primary"
              htmlType="submit"
              block
              size="large"
              loading={busy}
              style={{
                height: 44,
                borderRadius: 10,
                fontSize: 14,
                fontWeight: 600,
                background:
                  "linear-gradient(135deg, #36c5f0 0%, #00d4a3 100%)",
                border: "none",
                color: "#001018",
                boxShadow: "0 10px 26px rgba(54,197,240,0.35)",
                letterSpacing: 0.5,
              }}
            >
              Sign in
            </Button>
          </Form.Item>
        </Form>

        {/* Footer */}
        <div
          style={{
            textAlign: "center",
            marginTop: 22,
            fontSize: 11,
            color: "#2b3a63",
            letterSpacing: 0.5,
          }}
        >
          NetScope Console · sflow sensor platform
        </div>
      </div>
    </div>
  );
}
